<template>
  <div
    class="textarea-fi"
    :class="{
      'textarea-fi--focused': isFocused,
      'textarea-fi--active': isActiveFormElement,
      'textarea-fi--disabled': disabled,
      'textarea-fi--readonly': readonly,
      'textarea-fi--error': error || isValidationError,
      'textarea-fi--full-height': fullHeight,
      'textarea-fi--light': light,
      'textarea-fi--not-mounted': isNotMounted,
    }"
  >
    <div class="textarea-fi__field">
      <div
        :class="{
          'textarea-fi__field-container': true,
          'textarea-fi__field-container--grow': autoGrow,
        }"
        :style="autoGrow ? { maxHeight: maxGrowHeight + 'px' } : null"
        :data-replicated-value="autoGrow ? inputValue : null"
      >
        <label-fi
          class="textarea-fi__label"
          :label="label"
          :mandatory="required"
          :for="textareaId"
        />
        <textarea
          :id="textareaId"
          ref="inputRef"
          v-model="inputValue"
          :variant="getInputState"
          :maxlength="maxlength || undefined"
          :required="required"
          :disabled="disabled"
          :readonly="readonly || isNotMounted"
          :data-test="dataTest"
          :rows="rows || undefined"
          :class="{
            'textarea-fi__input': true,
            'textarea-fi__input--clearable': clear,
            'textarea-fi__input--no-resize': noResize,
          }"
          :placeholder="placeholder"
          @focus="focusInput"
          @blur="blurInput"
          @input="updateValue"
          @change="changeValue"
        />
        <icon-fi
          v-if="clear"
          class="textarea-fi__clear"
          :icon="clearIcon"
          @click="$emit('clear')"
        />
      </div>
      <div
        v-if="maxlength || isError || isValidationError"
        class="textarea-fi__info"
      >
        <span
          v-if="isError"
          class="textarea-fi__error-text"
        >
          {{ errorMessage }}
        </span>
        <span
          v-if="isValidationError"
          class="textarea-fi__error-text"
        >
          {{ errorText }}
        </span>
        <span
          v-if="maxlength"
          class="textarea-fi__length"
          >{{
            dict.get('online-Shop.Global-Labels.Input-Max-Length', [
              String(currentLength),
              String(maxlength),
            ])
          }}</span
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useId, useTemplateRef, watch } from 'vue';

import IconFi from '@/components/atoms/icon-fi/IconFi.vue';
import LabelFi from '@/components/atoms/label-fi/LabelFi.vue';
import { useDict } from '@/composables/fischerPlugin';
import useJssFormsConditionalField, {
  type DataFormElementKeyProps,
} from '@/composables/jssFormsConditionalField';
import { type Rule, useValidateFormElement } from '@/composables/validateFormElement';

interface Props extends DataFormElementKeyProps {
  value?: string;
  modelValue?: string;
  label?: string;
  placeholder?: string;
  maxlength?: number | null;
  error?: boolean;
  errorMessage?: string | undefined;
  required?: boolean;
  onlyEmitIfValid?: boolean;
  disabled?: boolean;
  dataTest?: string | null;
  rows?: number | null;
  fullHeight?: boolean;
  light?: boolean;
  readonly?: boolean;
  rules?: Rule[];
  trim?: boolean;
  id?: string;
  clear?: boolean;
  clearIcon?: string;
  autoGrow?: boolean;
  maxGrowHeight?: number;
  noResize?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  value: undefined,
  modelValue: undefined,
  label: '',
  placeholder: '',
  maxlength: null,
  error: false,
  errorMessage: undefined,
  required: false,
  onlyEmitIfValid: false,
  disabled: false,
  dataTest: null,
  rows: null,
  fullHeight: false,
  light: false,
  rules: () => [],
  trim: false,
  id: undefined,
  clearIcon: 'clear',
  autoGrow: false,
  maxGrowHeight: 250,
  noResize: false,
});

const emit = defineEmits<{
  blur: [];
  change: [value: string];
  'update:modelValue': [value: string];
  'validation-changed': [valid: boolean];
  clear: [];
}>();

const dict = useDict();
useJssFormsConditionalField(props);

const isFocused = ref(false);
const inputValue = ref('');
const currentLength = ref(0);
const inputRef = useTemplateRef<HTMLTextAreaElement>('inputRef');
const isNotMounted = ref(true);
const isActiveFormElement = ref(false);

// Generate unique ID for accessibility
const textareaId = props.id || useId();

const inputValueForValidation = computed(() => inputValue.value);
const { valid, errorText, validate } = useValidateFormElement({
  props,
  value: inputValueForValidation,
});

const isValidationError = computed(() => {
  return !valid.value && props.rules && props.rules.length > 0;
});

const isError = computed(() => {
  return props.error || errorText.value;
});

const getInputState = computed(() => {
  if (isError.value) {
    return 'error';
  }
  return '';
});

const setInput = (value = '') => {
  inputValue.value = value;
  currentLength.value = inputValue.value.length;

  if (inputRef.value && inputRef.value.value !== value) {
    inputRef.value.value = value;
  }

  if (value !== null && value !== undefined && value !== '') {
    isActiveFormElement.value = true;
  }
};

const initInput = () => {
  if (props.modelValue !== null && props.modelValue !== undefined) {
    setInput(props.modelValue);
  } else if (props.value !== null && props.value !== undefined) {
    setInput(props.value);
  }

  validate();
};

const focusInput = () => {
  if (props.readonly) {
    return;
  }
  isFocused.value = true;
  isActiveFormElement.value = true;
};

const blurInput = () => {
  isFocused.value = false;

  if (!inputValue.value) {
    isActiveFormElement.value = false;
  }

  emit('blur');
};

const updateValue = (evt: Event | string) => {
  const value = typeof evt === 'string' ? evt : (evt.target as HTMLTextAreaElement)?.value || '';
  setInput(value);

  if (props.onlyEmitIfValid && props.rules && !valid.value) {
    return;
  }

  emit('update:modelValue', inputValue.value);
};

const changeValue = (evt: Event | string) => {
  const value = typeof evt === 'string' ? evt : (evt.target as HTMLTextAreaElement)?.value || '';
  setInput(value);

  emit('change', inputValue.value);
};

watch(() => props.rules, validate);

watch(valid, (newValue) => {
  emit('validation-changed', newValue);
});

watch(
  () => props.value,
  (newValue) => {
    if (newValue !== undefined) {
      updateValue(newValue);
    }
  },
);

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue || newValue === '') {
      isActiveFormElement.value = false;
    } else {
      isActiveFormElement.value = true;
    }

    if (newValue !== undefined) {
      updateValue(newValue);
    }
  },
);

onMounted(() => {
  initInput();
  isNotMounted.value = false;
});
</script>

<style src="./textarea-fi" />
